import React, { useContext, useState } from 'react';
import { Form, Input, Select, Button, Space, Row, Typography, Divider } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { createDevice } from '../../../services/StationService';
import { isValidSerialNum } from '../../../Validation';
import { StationContext } from '../../../context/station-context';
import { Modal } from '../../UI/Modal/Modal.component';
import { StationProfile } from '../Station/StationContent/StationProfile';
import { saveProfileSettings } from '../../../services/StationService';
import { NocItemContext } from '../../../context/noc-item-context';
import { PermissionsContext } from '../../../router/DashboardRoutes';
import { useNotificationApi } from '../../UI/NotificationProvider';
import { Restricted } from '../../UI/Restricted.component';

const { Option } = Select;
const { Title, Paragraph, } = Typography;

export const AddNewDeviceModal = ({addNewDeviceModal, setAddNewDeviceModal, baseDevices, ...props}) => {

    const notificationApi = useNotificationApi();

    const userPermissions = useContext(PermissionsContext);
    const isPCI = userPermissions.server.pci;

    const [nocItem] = useContext(NocItemContext);
    const NONE_DEFAULT_IDS = nocItem?.profiles?.None || [];

    const [state, dispatch] = useContext(StationContext);
    const station = state.station;

    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const [displayProfileFields, setDisplayProfileFields] = useState(false);

    const onFinish = (data) => {
        Object.assign(data, {placeID: station.id});
        setLoading(true)
        createDevice(data).then( function(response) {
            if(response.result) {

                if(displayProfileFields){
                    const checkProfileId = (id) => {
                        // if id is from a default option return null
                        return NONE_DEFAULT_IDS.includes(id) ? null : id;
                    }

                    let profileSettingsObj = {
                        ownerID: station.id,
                        customerID: checkProfileId(data.Customer),
                        brandID: checkProfileId(data.Brand),
                        posID: checkProfileId(data.POS),
                        dispenserID: checkProfileId(data.Dispenser),
                        paymentID: checkProfileId(data.Payment),
                    }
                    
                    saveProfileSettings(profileSettingsObj).then((response) => {
                        if(response){
                            dispatch({
                                type: "UPDATE_STATION_INFORMATION", 
                                payload: {profileSettings: profileSettingsObj}
                            })
                        }
                    })
                }

                dispatch({
                    type: 'ADD_DEVICE', 
                    payload: response.device,
                })

                notificationApi.success({
                    message: 'Device created',
                    description: `${data.deviceType} - Serial Number: ${data.serialNum} has been successfully created.`,
                });
                setAddNewDeviceModal(false);

            } else {
                notificationApi.error({
                    message: 'Error creating device',
                    description: displayAlertMessage(response),
                    duration: 6,
                });
                
            }
            setLoading(false);
        })
    }

    const loadingMessage = (
        <>
            <Title level={5}>Creating device...</Title>
            <Paragraph>Please wait, this action may take a few minutes.</Paragraph>
        </>
    );

    const displayAlertMessage = (response) => {
        let errorMessage; 
        switch(response.message){
            case 'duplicate':
                errorMessage = `Serial number ${response.device.serialNum} already exists!`;
                break;
            case ' Serial Num is not valid \n ':
                errorMessage = 'Please enter a valid serial number'
                break;
            default: 
                errorMessage = response.message;
                break;
        }
        return errorMessage;
    }

    const displayProfileSelection = (changedValues, allValues) => {

        if(allValues?.deviceType === 'CSBASE' || allValues?.deviceType === 'SNSBASE'){
            setDisplayProfileFields(true);
        } else {
            setDisplayProfileFields(false);
        }

        if(changedValues?.deviceType === 'CSBASE' || changedValues?.deviceType === 'SNSBASE'){
            form.setFieldsValue({
                Customer: null, 
                Brand: null, 
                POS: null, 
                Dispenser: null, 
                Payment: null
            })
        }
    }

    const deviceTypeOptions = [
        { value: "AAP", label: "AvaLAN Access Point (AAP)" },
        { value: "CAP", label: "Cable Access Point (CAP)" },
        { value: "CS", label: "Cloud Switch (CS)" },
        { value: "CSBASE", label: "Cloud Switch Base (CS-BASE)" },
        { value: 'CSDFS', label: "Cloud Switch DFS (CS-DFS)" },
        { value: 'CSEVC', label: "Cloud Switch EVC (CS-EVC)" },
        { value: 'LTE', label: "LTE" },
        { value: 'LTEEVC', label: "LTE-EVC" },
        { value: 'LTEFMS', label: "LTE-FMS" },
        { value: "SNS", label: "Secure Network Service (SNS)" },
        { value: "SNSBASE", label: "Secure Network Service Base (SNS-BASE)" },
        { value: "SNSDFS", label: "Secure Network Service DFS (SNS-DFS)" },
        { value: "SNSGVR", label: "Secure Network Service GVR (SNS-GVR)" },
        { value: "SUSW", label: "Subscriber Unit (SUSW)" },
        { value: "other", label: "Other" },
    ]

    return (
        <>
            <Modal
                title="Add Device"
                justifyTitle="middle"
                visible={props.visible || addNewDeviceModal}
                setVisible={props.setVisible || setAddNewDeviceModal}
                actionGroup={false}
                loading={loading}
                loadingMessage={loadingMessage}
                closable={false}
                >
                <Form 
                    form={form}
                    name='add-new-device'
                    layout="vertical"    
                    onFinish={(values) => onFinish(values)} 
                    initialValues={{
                        requiredMarkValue: 'required',
                    }}
                    colon={true}
                    requiredMark= 'required'
                    autoComplete="off"
                    onValuesChange={(changedValues, allValues) => !baseDevices?.length > 0 ? displayProfileSelection(changedValues, allValues) : undefined}
                >
                    <Form.Item 
                        label="Device Type:" 
                        name="deviceType" 
                        rules={[
                            {
                                required: true,
                                message: 'Please assign a device type',
                            },
                        ]}
                        >
                        <Select 
                            showSearch
                            placeholder="Please select one"
                            options={deviceTypeOptions}
                            optionFilterProp="label"
                            filterOption={(input, option) => 
                                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Form.Item>
                    <Form.Item 
                        label="Cloud ID / Serial Number:" 
                        name="serialNum"
                        hasFeedback
                        tooltip='A Cloud ID / Serial Number is a unique identifier assigned to a device.'
                        rules={[
                                {
                                    required: true,
                                    message: 'Cloud ID/Serial Number is required.'
                                },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (isValidSerialNum(value)){
                                            return Promise.resolve();
                                        } 
                                        return Promise.reject(new Error('Must be 6-30 alphanumeric characters. No symbols allowed.'))
                                        },
                                    }),
                                ]}
                    >
                        <Input showCount placeholder="Ex: AB397S" onInput={e => e.target.value = e.target.value.toUpperCase()}/>
                    </Form.Item>

                    <Form.Item label="Device Name:" name="comment">
                        <Input/>
                    </Form.Item>

                    { isPCI && displayProfileFields && !baseDevices?.length > 0 && 
                        <Restricted to='server.pci'>
                            <Title level={5}>Station Profile</Title>
                            <Divider style={{marginTop: 5}}/>
                            <StationProfile displayFields={true} />
                        </Restricted>
                    }

                    <Row justify="end">
                        <Space>
                            <Form.Item noStyle>
                                <Button onClick={() => {setAddNewDeviceModal(false)}}>Cancel</Button>
                            </Form.Item>
                            <Form.Item noStyle>
                                <Button type="primary" htmlType="submit" icon={<SaveOutlined/>}>Create</Button>
                            </Form.Item>
                        </Space>
                    </Row>
                </Form>
            </Modal>
        </>
    );
}